import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { DropdownModule } from "primeng/dropdown";
import { IconFieldModule } from "primeng/iconfield";
import { InputIconModule } from "primeng/inputicon";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { SelectButtonModule } from "primeng/selectbutton";
import { TooltipModule } from "primeng/tooltip";

// Models
import {
  getPaymentMethodDisplayName,
  getPaymentMethodTypes,
  PaymentIntegrationFilterParams,
} from "../../models/payment-integration.model";

/**
 * Form controls interface for filters
 */
export interface PaymentIntegrationsFilterControls {
  clientFilterId: import("@angular/forms").FormControl<number | null>;
  clientFilterName: import("@angular/forms").FormControl<string | null>;
  clientFilterProvider: import("@angular/forms").FormControl<string | null>;
  clientFilterTestMode: import("@angular/forms").FormControl<boolean | null>;
  page: import("@angular/forms").FormControl<number>;
  pageSize: import("@angular/forms").FormControl<number>;
  orderBy: import("@angular/forms").FormControl<string | null>;
  order: import("@angular/forms").FormControl<"asc" | "desc">;
}

@Component({
  selector: "app-payment-integrations-filters",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DropdownModule,
    IconFieldModule,
    InputIconModule,
    InputNumberModule,
    InputTextModule,
    SelectButtonModule,
    TooltipModule,
  ],
  templateUrl: "./payment-integrations-filters.component.html",
  styleUrl: "./payment-integrations-filters.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentIntegrationsFiltersComponent implements OnInit, OnDestroy {
  @Input() filterForm!: FormGroup<PaymentIntegrationsFilterControls>;
  @Input() loading = false;
  @Input() drawerMode = true;

  @Output() filtersChanged = new EventEmitter<
    Partial<PaymentIntegrationFilterParams>
  >();
  @Output() clearFilters = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();

  // Payment method type options for dropdown
  readonly paymentMethodOptions = [
    { label: "All Providers", value: null },
    ...getPaymentMethodTypes().map((type) => ({
      label: getPaymentMethodDisplayName(type),
      value: type,
    })),
  ];

  // Test mode tristate options
  readonly testModeOptions = [
    { label: "All", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  ngOnInit(): void {
    this.setupFilterSubscriptions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupFilterSubscriptions(): void {
    // Emit filter changes when form values change
    this.filterForm.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((values) => {
        this.filtersChanged.emit(
          values as Partial<PaymentIntegrationFilterParams>,
        );
        this.persistFilters(values);
      });

    // Load persisted filters
    this.loadPersistedFilters();
  }

  private persistFilters(
    values: Partial<{
      clientFilterId: number | null;
      clientFilterName: string | null;
      clientFilterProvider: string | null;
      clientFilterTestMode: boolean | null;
    }>,
  ): void {
    try {
      localStorage.setItem(
        "paymentIntegrations.filters",
        JSON.stringify({
          clientFilterId: values.clientFilterId,
          clientFilterName: values.clientFilterName,
          clientFilterProvider: values.clientFilterProvider,
          clientFilterTestMode: values.clientFilterTestMode,
        }),
      );
    } catch {
      // Ignore localStorage errors
    }
  }

  private loadPersistedFilters(): void {
    try {
      const saved = localStorage.getItem("paymentIntegrations.filters");
      if (saved) {
        const filters = JSON.parse(saved) as {
          clientFilterId?: number | null;
          clientFilterName?: string | null;
          clientFilterProvider?: string | null;
          clientFilterTestMode?: boolean | null;
        };

        this.filterForm.patchValue(
          {
            clientFilterId: filters.clientFilterId ?? null,
            clientFilterName: filters.clientFilterName ?? null,
            clientFilterProvider: filters.clientFilterProvider ?? null,
            clientFilterTestMode: filters.clientFilterTestMode ?? null,
          },
          { emitEvent: false },
        );
      }
    } catch {
      // Ignore localStorage errors
    }
  }

  onClearFilters(): void {
    this.filterForm.patchValue({
      clientFilterId: null,
      clientFilterName: null,
      clientFilterProvider: null,
      clientFilterTestMode: null,
      page: 1,
    });

    // Clear persisted filters
    try {
      localStorage.removeItem("paymentIntegrations.filters");
    } catch {
      // Ignore localStorage errors
    }

    this.clearFilters.emit();
  }

  /**
   * Check if any filters are active
   */
  get hasActiveFilters(): boolean {
    const values = this.filterForm.value;
    return !!(
      values.clientFilterId !== null ||
      values.clientFilterName?.trim() ||
      values.clientFilterProvider ||
      values.clientFilterTestMode !== null
    );
  }
}
